import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import {
  closureSummarySections,
  hasClosureSummaryContent,
  readClosureSummary,
  resolveResultLabelDisplay,
  resolveResultStatusFromArtifact,
  type ClosureSummaryFields,
} from '../../domain/safetyCaseClosure/safetyCaseClosure';
import { resolveExternalClosureResultLabel, sanitizeExternalLabel } from '../../domain/externalReview/externalReportLanguage';

type ClosureSummaryArtifactSectionProps = {
  scopeOrPayload?: Record<string, unknown> | null;
  closureSummary?: ClosureSummaryFields | null;
  persistedReport?: {
    report_payload?: Record<string, unknown> | null;
    report_content?: Record<string, unknown> | null;
    finalized_payload?: Record<string, unknown> | null;
  } | null;
  externalSafe?: boolean;
  isDemoOrLoopedSource?: boolean;
};

export function resolveClosureSummaryFromArtifact(
  persistedReport?: {
    report_payload?: Record<string, unknown> | null;
    report_content?: Record<string, unknown> | null;
    finalized_payload?: Record<string, unknown> | null;
  } | null,
): ClosureSummaryFields {
  const content = persistedReport?.report_content
    || persistedReport?.finalized_payload
    || persistedReport?.report_payload;
  if (!content || typeof content !== 'object') return {};

  const payload = content as Record<string, unknown>;
  const direct = payload.closure_summary;
  if (direct && typeof direct === 'object') {
    const parsed = readClosureSummary({ closure_summary: direct });
    if (hasClosureSummaryContent(parsed)) return parsed;
  }

  const workflowSummary = payload.workflow_summary;
  if (workflowSummary && typeof workflowSummary === 'object') {
    const fromWorkflow = (workflowSummary as Record<string, unknown>).closure_summary;
    if (fromWorkflow && typeof fromWorkflow === 'object') {
      const parsed = readClosureSummary({ closure_summary: fromWorkflow });
      if (hasClosureSummaryContent(parsed)) return parsed;
    }
  }

  return {};
}

export const ClosureSummaryArtifactSection: React.FC<ClosureSummaryArtifactSectionProps> = ({
  scopeOrPayload,
  closureSummary,
  persistedReport,
  externalSafe = false,
  isDemoOrLoopedSource = false,
}) => {
  const fields = closureSummary && hasClosureSummaryContent(closureSummary)
    ? closureSummary
    : hasClosureSummaryContent(readClosureSummary(scopeOrPayload))
      ? readClosureSummary(scopeOrPayload)
      : resolveClosureSummaryFromArtifact(persistedReport);
  const explicitResultStatus = resolveResultStatusFromArtifact(persistedReport);
  const resultLabel = externalSafe
    ? resolveExternalClosureResultLabel(resolveResultLabelDisplay(explicitResultStatus), {
        isDemo: isDemoOrLoopedSource,
      })
    : resolveResultLabelDisplay(explicitResultStatus);
  if (!hasClosureSummaryContent(fields) && !resultLabel) return null;

  const sections = closureSummarySections(fields).map((section) => ({
    ...section,
    text: externalSafe
      ? sanitizeExternalLabel(section.text, { isDemo: isDemoOrLoopedSource })
      : section.text,
  }));

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 3, mb: 3 }}
      data-testid="closure-summary-artifact-section"
    >
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          {externalSafe ? 'Corrective action logged' : 'Closure Summary'}
        </Typography>
        <Stack spacing={1.25}>
          {resultLabel ? (
            <Box data-testid="follow-up-result-artifact-line">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {externalSafe ? 'Follow-up status' : 'Follow-up result'}
              </Typography>
              <Typography variant="body2">{resultLabel}</Typography>
            </Box>
          ) : null}
          {externalSafe && isDemoOrLoopedSource ? (
            <Typography variant="body2" color="text.secondary">
              Follow-up measurement not evaluated on controlled demo footage. Exposure reduction is not claimed from
              this report.
            </Typography>
          ) : null}
          {sections.map((section) => (
            <Box key={section.label}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {section.label}
              </Typography>
              <Typography variant="body2">{section.text}</Typography>
            </Box>
          ))}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ClosureSummaryArtifactSection;
